// Streaming playback for an owned (or membership-included) product. Signed
// URLs are issued server-side per request — the storage path never reaches
// the browser and the client cannot assert access.
import { useState } from "react";
import { useServerFn } from "@tanstack/react-start";
import { requestStream, type StreamGrant } from "@/lib/ownership.functions";

export function ProductStreamPlayer({ slug }: { slug: string }) {
  const fetchStream = useServerFn(requestStream);
  const [tracks, setTracks] = useState<StreamGrant[] | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function load() {
    setLoading(true);
    setError(null);
    try {
      const res = await fetchStream({ data: { slug } });
      if ("error" in res) setError(res.error);
      else setTracks(res.tracks);
    } catch {
      setError("Playback could not be prepared. Please try again.");
    } finally {
      setLoading(false);
    }
  }

  if (tracks?.length) {
    return (
      <div className="space-y-4">
        {tracks.map((t) => (
          <div key={`${t.label}-${t.url.slice(-12)}`} className="rounded-lg border hairline bg-charcoal/60 p-3">
            <p className="mb-2 text-sm text-foreground">{t.label}</p>
            {t.kind === "video" ? (
              <video src={t.url} controls preload="none" className="w-full rounded" />
            ) : (
              <audio src={t.url} controls preload="none" className="w-full" aria-label={`Play ${t.label}`} />
            )}
          </div>
        ))}
      </div>
    );
  }

  return (
    <div>
      <button
        type="button"
        onClick={() => void load()}
        disabled={loading}
        className="rounded-full border hairline px-6 py-3 text-sm text-foreground transition hover:border-gold hover:text-gold disabled:opacity-50"
      >
        {loading ? "Preparing playback…" : "Stream now"}
      </button>
      {error && <p className="mt-3 text-xs text-rose-400">{error}</p>}
    </div>
  );
}
